import { OpportunityServicePromiseClient } from '@taxfyle/api-internal/internal/opportunity_grpc_web_pb'
import {
  GetOpportunityRequest,
  ListOpportunitiesRequest,
  ListQualifiedProvidersRequest,
  ApplyToOpportunityRequest,
  WithdrawApplicationRequest,
  GetCandidaciesRequest,
  ListOpportunityOffersRequest,
  DeclineOpportunityOfferRequest,
} from '@taxfyle/api-internal/internal/opportunity_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import memoize from 'memoizee'
import { Observable } from 'rxjs'
import { filter, share } from 'rxjs/operators'
import { APIConfig } from 'types/definitions'
import { enableDevTools } from 'utils/grpc-util'
import TypeCase = RealtimeEvent.TypeCase
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'

/**
 * Creates an instance of the Ops API.
 *
 * @param config
 * @param events$
 */
export function createOpportunitiesAPI(
  config: APIConfig,
  events$: Observable<RealtimeEvent>
) {
  return new OpportunitiesAPI(config, events$)
}

/**
 * Convenience wrapper around the gRPC clients.
 */
export class OpportunitiesAPI {
  private readonly opsClient = createOpportunitiesClient(this.config.baseURL)
  private readonly grpcAuthenticator = createGrpcAuthenticator(this.config)

  /**
   * Stream of Opportunity-related events.
   */
  readonly events$: Observable<RealtimeEvent>

  /**
   * Stream of Opportunity Offer-related events.
   */
  readonly offerEvents$: Observable<RealtimeEvent>

  /**
   * Constructor.
   */
  constructor(private config: APIConfig, events$: Observable<RealtimeEvent>) {
    this.grpcAuthenticator = createGrpcAuthenticator(this.config)

    // Opportunity events.
    this.events$ = events$.pipe(
      filter((event) => {
        // Filter out non-op events.
        switch (event.getTypeCase()) {
          case TypeCase.OPPORTUNITY_UPDATED:
          case TypeCase.OPPORTUNITY_REMOVED:
            return true
        }

        return false
      }),
      share()
    )

    // Opportunity Offer events.
    this.offerEvents$ = events$.pipe(
      filter((event) => {
        switch (event.getTypeCase()) {
          case TypeCase.OPPORTUNITY_OFFERED:
          case TypeCase.OPPORTUNITY_OFFER_REMOVED:
            return true
        }

        return false
      }),
      share()
    )
  }

  /**
   * List qualified providers.
   *
   * @param request
   */
  async listQualifiedProviders(request: ListQualifiedProvidersRequest) {
    return this.opsClient.listQualifiedProviders(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * List opportunities.
   *
   * @param request
   */
  async listOpportunities(request: ListOpportunitiesRequest) {
    return this.opsClient.listOpportunities(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * Get opportunity.
   *
   * @param request
   */
  async getOpportunity(request: GetOpportunityRequest) {
    return this.opsClient.getOpportunity(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * Apply for an opportunity.
   */
  async apply(request: ApplyToOpportunityRequest) {
    return this.opsClient.applyToOpportunity(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * Withdraw an application for an opportunity.
   */
  async withdrawApplication(request: WithdrawApplicationRequest) {
    return this.opsClient.withdrawApplication(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * Queries for the candidacies for the user.
   */
  async getCandidacies(request: GetCandidaciesRequest) {
    return this.opsClient.getCandidacies(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * Lists Opportunity Offers for the user.
   */
  async listOpportunityOffers(request: ListOpportunityOffersRequest) {
    return this.opsClient.listOpportunityOffers(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * Gets the requested Opportunity Offer.
   */
  async getOpportunityOffer(request: GetOpportunityRequest) {
    return this.opsClient.getOpportunityOffer(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }

  /**
   * Declines the offered opportunity.
   */
  async declineOffer(request: DeclineOpportunityOfferRequest) {
    return this.opsClient.declineOpportunityOffer(
      request,
      await this.grpcAuthenticator.getMeta()
    )
  }
}

/**
 * Creates an Opportunities client that is memoized.
 */
const createOpportunitiesClient = memoize(
  function createOpportunitiesGrpcClient(baseUrl: string) {
    const client = new OpportunityServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  }
)
